import { ChangeDetectionStrategy, Component, inject, input, OnInit, output } from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { ProjectFlowlineParameter } from "@app/shared/models/project-flowline-parameter";
import { DropDownListComponent } from "@progress/kendo-angular-dropdowns";
import { NumericTextBoxComponent } from "@progress/kendo-angular-inputs";
import { zip as observableZip } from "rxjs";
import { LookupPlusService } from "../../services/lookups/lookup-plus.service";
import { ProjectCustomEndpointService } from "../../services/project-custom-endpoint.service";
import { ProjectEstimateService } from "../../services/project-estimate.service";
import { ProjectFlowlineTypeDetail } from "./../../../../shared/models/project-flowline-type-detail";

@Component({
  selector: "app-project-flowline-details",
  templateUrl: "./project-flowline-details.component.html",
  styleUrls: ["./project-flowline-details.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    DropDownListComponent,
    FormsModule,
    NumericTextBoxComponent,
    ReactiveFormsModule,
  ],
})
export class ProjectFlowlineDetailsComponent implements OnInit {
  private fb = inject(UntypedFormBuilder);
  private lookupPlusService = inject(LookupPlusService);
  private projectCustomEndpointService = inject(ProjectCustomEndpointService);
  private estimationService = inject(ProjectEstimateService);

  public decimalPrecision: number = 0;
  public decimalFormat: string = "n0";
  public decimalFormat2: string = "n2";
  public decimalPrecision2: number = 2;
  public flowlineForm: UntypedFormGroup;
  public flowlineTypeDetails: ProjectFlowlineTypeDetail[];
  public flowlineParameters: ProjectFlowlineParameter;
  public lineDiameters: Array<number> = [0, 4, 6, 8];
  public boreSizes: Array<number> = [0, 6, 8, 10, 12, 14, 16, 20];

  readonly tabTdv = output<boolean>();
  readonly iProjectId = input<number>(undefined);
  readonly iProjectTypeId = input<number>(undefined);
  readonly iProjectStatusId = input<number>(undefined);

  ngOnInit() {
    this.generateForm();
    this.getFlowlineParameters();
  }

  get flowLineValues(): UntypedFormArray {
    return this.flowlineForm.get("flowlineTypeData") as UntypedFormArray;
  }

  public updateFlowlineParameters(e): void {
    const requests = [];
    this.flowlineForm.controls.flowlineTypeData.value.forEach((v) => {
      let patch: any = {
        BoreLength: v.BoreLength,
        BoreSize: v.BoreSize,
        FenceGaps: v.FenceGaps,
        Gates: v.Gates,
        LineCount: v.LineCount,
        LineCrossings: v.LineCrossings,
        LineDiameter: v.LineDiameter,
        NewDitch: v.NewDitch,
        Reseeding: v.Reseeding,
        RightOfWay: v.RightOfWay,
        Risers: v.Risers,
        TotalLength: v.TotalLength,
        ValveStation: v.ValveStation,
      };
      requests.push(this.projectCustomEndpointService.patch(`ProjectFlowlineTypeDetails(${v.ProjectFlowlineTypeDetailId})`, patch));
    });
    this.flowlineParameters.RockDitchingPercent = this.flowlineForm.controls.RockDitchingPercent.value;
    this.flowlineParameters.TwoInchSDRBoxLengthFT = this.flowlineForm.controls.TwoInchSDRBoxLengthFT.value;
    this.flowlineParameters.ThreeInchSDRBoxLengthFT = this.flowlineForm.controls.ThreeInchSDRBoxLengthFT.value;
    requests.push(this.projectCustomEndpointService.patch(`ProjectFlowlineParameters(${this.flowlineParameters.ProjectFlowlineParameterId})`, this.flowlineParameters));
    observableZip(...requests).subscribe((response) => {
      this.flowlineForm.reset();
      this.getFlowlineParameters();
    });
  }

  public cancelChanges(e): void {
    this.flowlineForm.reset();
    this.getFlowlineParameters();
  }

  public setTDV(): void {
    let tdvState: boolean = true;
    this.tabTdv.emit(tdvState);
  }

  public clearTDV(): void {
    let tdvState: boolean = false;
    this.tabTdv.emit(tdvState);
  }

  private generateForm(): void {
    this.flowlineForm = this.fb.group({
      RockDitchingPercent: null,
      TwoInchSDRBoxLengthFT: null,
      ThreeInchSDRBoxLengthFT: null,
      flowlineTypeData: this.fb.array([]),
    });
    this.flowlineForm.valueChanges.subscribe((data) => this.onValueChanged(data));
  }

  private onValueChanged(data?: any) {
    let isValid: boolean = false;

    if (!this.flowlineForm) {
      return;
    }

    if (this.flowlineForm.valid && this.flowlineForm.dirty) {
      isValid = true;
      this.setTDV();
    } else {
      isValid = false;
      this.clearTDV();
    }
  }

  private getFlowlineParameters(): void {
    this.estimationService.calculateFlowlineParameters(this.iProjectId()).then(() => {
      this.lookupPlusService.getLookup("ProjectFlowlineParameters", `&$filter=ProjectId eq ${this.iProjectId()}`).then((payload) => {
        this.flowlineParameters = payload.value[0];
        this.setFlowlineParameters();
      });
      this.lookupPlusService.getLookup("ProjectFlowlineTypeDetails", `&$filter=ProjectId eq ${this.iProjectId()}`).then((payload) => {
        this.flowlineTypeDetails = payload.value;
        this.setAdditionalLength(this.flowlineTypeDetails);
        this.setFlowlineTypeData();
      });
    });
  }

  private setAdditionalLength(flowlineTypeDetails: ProjectFlowlineTypeDetail[]): void {
    flowlineTypeDetails.forEach((flowlineTypeDetail) => {
      if (flowlineTypeDetail.TotalLength == null || flowlineTypeDetail.NewDitch == null) {
        flowlineTypeDetail.AdditionalLength = null;
      } else {
        flowlineTypeDetail.AdditionalLength = flowlineTypeDetail.TotalLength - flowlineTypeDetail.NewDitch;
      }
    });
  }

  private setFlowlineTypeData(): void {
    const sortIndex = [0, 3, 4, 5, 1, 2];
    const ovFormGroups = sortIndex.map((i) => this.flowlineTypeDetails[i]).map((override) => this.fb.group(override));
    const overrideFormArray = this.fb.array(ovFormGroups);
    this.flowlineForm.setControl("flowlineTypeData", overrideFormArray);
  }
  private setFlowlineParameters(): void {
    if (this.flowlineParameters == null) return;
    this.flowlineForm.patchValue({
      RockDitchingPercent: this.flowlineParameters.RockDitchingPercent,
      TwoInchSDRBoxLengthFT: this.flowlineParameters.TwoInchSDRBoxLengthFT,
      ThreeInchSDRBoxLengthFT: this.flowlineParameters.ThreeInchSDRBoxLengthFT,
    });
  }
}